import React, { useMemo } from "react";
import {
  Box,
  Container,
  ImageList,
  ImageListItem,
  Stack,
  Typography,
  CircularProgress
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import UserQuery from "../../stateQueries/User";

const API_URL = process.env.REACT_APP_API_URL;

const Gallery = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["galleryImages"],
    queryFn: async () => await UserQuery.getAllGalleryImages()
  });

  // Process galleries - keep them grouped with title and description
  const galleries = useMemo(() => {
    if (!data?.galleries || data.galleries.length === 0) return [];

    // Sort galleries by createdAt (newest first)
    return [...data.galleries].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [data]);

  if (isLoading) {
    return (
      <Box
        sx={{
          py: 4,
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError || galleries.length === 0) {
    return (
      <Box sx={{ py: 4, minHeight: "100vh" }}>
        <Container maxWidth="xl">
          <Stack spacing={4} alignItems="center" justifyContent="center">
            <Typography variant="h6" color="text.secondary">
              No gallery images available
            </Typography>
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4, minHeight: "100vh" }}>
      <Container maxWidth="xl">
        <Stack spacing={4}>
          {/* Header */}
          <Box textAlign="center">
            <Typography
              variant="h3"
              fontWeight="bold"
              sx={{
                color: "primary.main",
                mb: 1,
                fontSize: { xs: "2rem", md: "3rem" }
              }}
            >
              GALLERY
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: "text.secondary", maxWidth: 600, mx: "auto" }}
            >
              Explore our collection of memorable moments from FASSET events
            </Typography>
          </Box>

          {/* Gallery Sections - grouped by gallery */}
          {galleries.map((gallery) => (
            <Box key={gallery.id}>
              {/* Gallery Title and Description */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ color: "primary.main" }}
                >
                  {gallery.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {gallery.description}
                </Typography>
              </Box>

              {/* Desktop Gallery - 4 columns */}
              <Box display={{ xs: "none", md: "block" }}>
                <ImageList cols={4} gap={20}>
                  {gallery.images.map((imageName, index) => (
                    <ImageListItem
                      key={index}
                      sx={{
                        cursor: "pointer",
                        overflow: "hidden",
                        borderRadius: 2,
                        boxShadow: 3,
                        transition: "all 0.3s ease-in-out",
                        "&:hover": {
                          transform: "scale(1.05)",
                          boxShadow: 6
                        }
                      }}
                    >
                      <img
                        src={`${API_URL}/uploads/gallery/${imageName}`}
                        alt={`${gallery.title} - ${index + 1}`}
                        loading="lazy"
                        style={{
                          height: 350,
                          objectFit: "cover",
                          objectPosition: "center"
                        }}
                        onClick={() => {
                          window.open(
                            `${API_URL}/uploads/gallery/${imageName}`,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              </Box>

              {/* Mobile Gallery - 2 columns */}
              <Box display={{ xs: "block", md: "none" }}>
                <ImageList cols={2} gap={10}>
                  {gallery.images.map((imageName, index) => (
                    <ImageListItem
                      key={index}
                      sx={{
                        cursor: "pointer",
                        overflow: "hidden",
                        borderRadius: 2,
                        boxShadow: 3
                      }}
                    >
                      <img
                        src={`${API_URL}/uploads/gallery/${imageName}`}
                        alt={`${gallery.title} - ${index + 1}`}
                        loading="lazy"
                        style={{
                          height: 200,
                          objectFit: "cover",
                          objectPosition: "center"
                        }}
                        onClick={() => {
                          window.open(
                            `${API_URL}/uploads/gallery/${imageName}`,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              </Box>
            </Box>
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default Gallery;
